import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import templateService from "../../../services/template.service";

const AdminTemplateDashboard = ({ item }) => {
    const { clinicId } = useParams();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 0,
        totalPages: 1,
        totalElements: 0,
        pageSize: 50
    });

    // Function to fetch templates using templateService
    const getEcxTemplate = async (page = 0, size = 50) => {
        try {
            setLoading(true);
            setError(null);

            // Call the template service
            const response = await templateService.getEcxTemplate(page, size);

            setTemplates(response.data.content);
            setPagination({
                currentPage: response.data.number,
                totalPages: response.data.totalPages,
                totalElements: response.data.totalElements,
                pageSize: response.data.size
            });
        } catch (err) {
            setError("Failed to load templates. Please try again.");
            console.error("Error fetching templates:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getEcxTemplate();
    }, [clinicId]);

    // Filter templates by medium type
    const emailTemplates = templates.filter(template => template.mediumType === "EMAIL");
    const smsTemplates = templates.filter(template => template.mediumType === "SMS");
    const callTemplates = templates.filter(template => template.mediumType === "CALL");

    // Render template section
    const renderTemplateSection = (title, templates, mediumType) => (
        <div className="mb-5" key={mediumType}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="text-primary">
                    {title} <span className="badge bg-secondary">{templates.length}</span>
                </h3>
            </div>

            {templates.length === 0 ? (
                <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    No {title.toLowerCase()} templates found.
                </div>
            ) : (
                <div className="row">
                    {templates.map(template => (
                        <div key={template.id} className="col-md-6 col-lg-4 mb-3">
                            <div className="card h-100 shadow-sm">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h5 className="card-title mb-0 text-primary">{template.reminderName}</h5>
                                    <span className={`badge ${template.usedBy === 'CLINIC' ? 'bg-success' : 'bg-warning'}`}>
                    {template.usedBy}
                  </span>
                                </div>
                                <div className="card-body">
                                    <p className="card-text text-muted">{template.content}</p>
                                </div>
                                <div className="card-footer bg-transparent">
                                    <small className="text-muted d-block">
                                        <i className="bi bi-calendar-plus me-1"></i>
                                        Created: {template.createdAt}
                                    </small>
                                    {template.modifiedAt !== template.createdAt && (
                                        <small className="text-muted d-block mt-1">
                                            <i className="bi bi-calendar-check me-1"></i>
                                            Updated: {template.modifiedAt}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // Render pagination controls
    const renderPagination = () => {
        if (pagination.totalPages <= 1) return null;

        return (
            <div className="d-flex justify-content-between align-items-center mb-4 p-3 bg-light rounded">
                <div>
                    <button
                        onClick={() => getEcxTemplate(pagination.currentPage - 1, pagination.pageSize)}
                        disabled={pagination.currentPage === 0}
                        className="btn btn-outline-primary me-2"
                    >
                        <i className="bi bi-chevron-left me-1"></i>
                        Previous
                    </button>

                    <button
                        onClick={() => getEcxTemplate(pagination.currentPage + 1, pagination.pageSize)}
                        disabled={pagination.currentPage >= pagination.totalPages - 1}
                        className="btn btn-outline-primary"
                    >
                        Next
                        <i className="bi bi-chevron-right ms-1"></i>
                    </button>
                </div>

                <div className="text-center">
          <span className="fw-bold text-muted">
            Page {pagination.currentPage + 1} of {pagination.totalPages}
          </span>
                    <br />
                    <small className="text-muted">
                        Showing {templates.length} of {pagination.totalElements} templates
                    </small>
                </div>

                <div>
                    <select
                        onChange={(e) => getEcxTemplate(0, parseInt(e.target.value))}
                        value={pagination.pageSize}
                        className="form-select"
                        style={{ width: 'auto' }}
                    >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                    </select>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading templates...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger mx-3" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
                <button
                    className="btn btn-sm btn-outline-danger ms-3"
                    onClick={() => getEcxTemplate(pagination.currentPage, pagination.pageSize)}
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2 className="text-primary">
                            <i className="bi bi-grid me-2"></i>
                            ECX Templates Dashboard
                        </h2>
                        <button
                            className="btn btn-primary"
                            onClick={() => getEcxTemplate(pagination.currentPage, pagination.pageSize)}
                        >
                            <i className="bi bi-arrow-clockwise me-1"></i>
                            Refresh
                        </button>
                    </div>

                    {renderPagination()}

                    <div className="templates-container">
                        {renderTemplateSection("Email Templates", emailTemplates, "EMAIL")}
                        {renderTemplateSection("SMS Templates", smsTemplates, "SMS")}
                        {renderTemplateSection("Call Templates", callTemplates, "CALL")}
                    </div>

                    {renderPagination()}

                    {templates.length === 0 && !loading && (
                        <div className="text-center py-5">
                            <i className="bi bi-inbox display-1 text-muted"></i>
                            <h4 className="text-muted mt-3">No templates found</h4>
                            <p className="text-muted">There are no templates available.</p>
                            <button
                                className="btn btn-primary mt-2"
                                onClick={() => getEcxTemplate(0, pagination.pageSize)}
                            >
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                Try Again
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminTemplateDashboard;